import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiMail, FiPhone, FiMapPin } from 'react-icons/fi';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API_URL}/api/demo-request`, formData);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', company: '', phone: '', message: '' });
      }, 5000);
    } catch (err) {
      console.error('Demo request failed:', err);
      setError(t('landing.contactSection.submitError'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const whyUsReasons = t('landing.contact.whyUs.reasons', { returnObjects: true });

  return (
    <section id="contact" className="py-20 bg-white dark:bg-surface-900" aria-label={t('landing.contactSection.ariaLabel')}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-extrabold text-gray-900 dark:text-surface-50 mb-4" data-testid="contact-title">
            {t('landing.contact.title')}
          </h2>
          <p className="text-xl text-gray-600 dark:text-surface-400" data-testid="contact-subtitle">
            {t('landing.contact.subtitle')}
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-surface-50 mb-6">{t('landing.contact.info.title')}</h3>
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="bg-blue-100 rounded-lg p-3 mr-4">
                  <FiMail className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-surface-50">{t('landing.contact.info.email')}</p>
                  <p className="text-gray-600 dark:text-surface-400">info@runvya.com</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-blue-100 rounded-lg p-3 mr-4">
                  <FiPhone className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-surface-50">{t('landing.contact.info.phone')}</p>
                  <p className="text-gray-600 dark:text-surface-400">+90 232 433 1 435</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-blue-100 rounded-lg p-3 mr-4">
                  <FiMapPin className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-surface-50">{t('landing.contact.info.address')}</p>
                  <p className="text-gray-600 dark:text-surface-400">Fatih Mah. 1185 sk. No:5/C<br />Gaziemir/İZMİR</p>{/* i18n-ignore: postal address */}
                </div>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 dark:text-surface-50 mb-3">{t('landing.contact.whyUs.title')}</h4>
              <ul className="space-y-2 text-gray-600 dark:text-surface-400">
                {Array.isArray(whyUsReasons) && whyUsReasons.map((reason, index) => (
                  <li key={index}>✓ {reason}</li>
                ))}
              </ul>
            </div>
          </div>
          <div>
            {submitted ? (
              <div className="bg-green-50 border-2 border-green-500 rounded-xl p-8 text-center" data-testid="success-message">
                <div className="text-green-500 text-6xl mb-4">✓</div>
                <h3 className="text-2xl font-bold text-green-900 mb-2">{t('landing.contact.form.success.title')}</h3>
                <p className="text-green-700">{t('landing.contact.form.success.message')}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-gray-50 dark:bg-surface-900 rounded-xl p-8 shadow-lg" data-testid="contact-form">
                <div className="mb-6">
                  <label className="block text-gray-700 dark:text-surface-200 font-semibold mb-2" htmlFor="name">
                    {t('landing.contact.form.name')} *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-surface-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="contact-name"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-gray-700 dark:text-surface-200 font-semibold mb-2" htmlFor="email">
                    {t('landing.contact.form.email')} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-surface-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="contact-email"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-gray-700 dark:text-surface-200 font-semibold mb-2" htmlFor="company">
                    {t('landing.contact.form.company')} *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-surface-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="contact-company"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-gray-700 dark:text-surface-200 font-semibold mb-2" htmlFor="phone">
                    {t('landing.contact.form.phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-surface-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="contact-phone"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-gray-700 dark:text-surface-200 font-semibold mb-2" htmlFor="message">
                    {t('landing.contact.form.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-surface-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="contact-message"
                  ></textarea>
                </div>
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition duration-300 shadow-lg ${
                    loading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('landing.contactSection.submitting')}
                    </span>
                  ) : (
                    t('landing.contact.form.submit')
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
